import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { FINE_POINTER_QUERY } from '../../../hooks/useFinePointer';
import styles from './GlassSurface.module.css';

interface GlassSurfaceProps {
    children?: ReactNode;
    borderRadius?: number;
    className?: string;
}

const EDGE_WIDTH = 0.07;
const BRIGHTNESS = 50;
const OPACITY = 0.93;
const EDGE_BLUR = 11;
const DISTORTION_SCALE = -180;
const CHANNEL_OFFSETS = { red: 0, green: 10, blue: 20 } as const;

function displacementMapUrl(width: number, height: number, radius: number, gradientId: string): string {
    const edge = Math.min(width, height) * (EDGE_WIDTH * 0.5);
    const svg = `<svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="${gradientId}-r" x1="100%" y1="0%" x2="0%" y2="0%"><stop offset="0%" stop-color="#0000"/><stop offset="100%" stop-color="red"/></linearGradient><linearGradient id="${gradientId}-b" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#0000"/><stop offset="100%" stop-color="blue"/></linearGradient></defs><rect width="${width}" height="${height}" fill="black"/><rect width="${width}" height="${height}" rx="${radius}" fill="url(#${gradientId}-r)"/><rect width="${width}" height="${height}" rx="${radius}" fill="url(#${gradientId}-b)" style="mix-blend-mode:difference"/><rect x="${edge}" y="${edge}" width="${width - edge * 2}" height="${height - edge * 2}" rx="${radius}" fill="hsl(0 0% ${BRIGHTNESS}% / ${OPACITY})" style="filter:blur(${EDGE_BLUR}px)"/></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function supportsSvgBackdrop(filterId: string): boolean {
    if (!window.matchMedia(FINE_POINTER_QUERY).matches) return false;
    const userAgent = navigator.userAgent;
    const isSafari = /Safari/.test(userAgent) && !/Chrome|Chromium/.test(userAgent);
    if (isSafari || /Firefox/.test(userAgent)) return false;
    const probe = document.createElement('div');
    probe.style.backdropFilter = `url(#${filterId})`;
    return probe.style.backdropFilter !== '';
}

export default function GlassSurface({ children, borderRadius = 999, className }: GlassSurfaceProps) {
    const filterId = `glass-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<SVGFEImageElement>(null);
    const [refracts, setRefracts] = useState(false);

    const updateMap = useCallback(() => {
        const bounds = containerRef.current?.getBoundingClientRect();
        if (!bounds || !imageRef.current) return;
        const radius = Math.min(borderRadius, bounds.height / 2);
        imageRef.current.setAttribute('href', displacementMapUrl(bounds.width, bounds.height, radius, filterId));
    }, [borderRadius, filterId]);

    useEffect(() => {
        setRefracts(supportsSvgBackdrop(filterId));
    }, [filterId]);

    useEffect(() => {
        const container = containerRef.current;
        if (!refracts || !container) return;
        updateMap();
        const observer = new ResizeObserver(updateMap);
        observer.observe(container);
        return () => observer.disconnect();
    }, [refracts, updateMap]);

    const style = { borderRadius: `${borderRadius}px`, '--glass-filter': `url(#${filterId})` } as CSSProperties;

    return (
        <div ref={containerRef} className={`${styles.surface} ${refracts ? styles.refracting : styles.frosted} ${className ?? ''}`} style={style}>
            {refracts && (
                <svg className={styles.filter} aria-hidden="true" focusable="false">
                    <defs>
                        <filter id={filterId} colorInterpolationFilters="sRGB" x="0%" y="0%" width="100%" height="100%">
                            <feImage ref={imageRef} x="0" y="0" width="100%" height="100%" preserveAspectRatio="none" result="map" />
                            <feDisplacementMap in="SourceGraphic" in2="map" scale={DISTORTION_SCALE + CHANNEL_OFFSETS.red} xChannelSelector="R" yChannelSelector="G" result="displacedRed" />
                            <feColorMatrix in="displacedRed" type="matrix" values="1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0" result="red" />
                            <feDisplacementMap in="SourceGraphic" in2="map" scale={DISTORTION_SCALE + CHANNEL_OFFSETS.green} xChannelSelector="R" yChannelSelector="G" result="displacedGreen" />
                            <feColorMatrix in="displacedGreen" type="matrix" values="0 0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 1 0" result="green" />
                            <feDisplacementMap in="SourceGraphic" in2="map" scale={DISTORTION_SCALE + CHANNEL_OFFSETS.blue} xChannelSelector="R" yChannelSelector="G" result="displacedBlue" />
                            <feColorMatrix in="displacedBlue" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 1 0 0 0 0 0 1 0" result="blue" />
                            <feBlend in="red" in2="green" mode="screen" result="redGreen" />
                            <feBlend in="redGreen" in2="blue" mode="screen" result="output" />
                            <feGaussianBlur in="output" stdDeviation="0.7" />
                        </filter>
                    </defs>
                </svg>
            )}
            <div className={styles.content}>{children}</div>
        </div>
    );
}
